/**
 * Dual-write storage adapter for Supabase Auth.
 *
 * Writes to both localStorage (fast) and IndexedDB (durable).
 * On read, tries localStorage first; if empty, recovers from IndexedDB
 * and re-hydrates localStorage for fast subsequent reads.
 *
 * This solves the iOS PWA issue where localStorage is evicted when
 * the OS terminates the WebKit process under memory pressure.
 */

const DB_NAME = "ez-auth";
const STORE_NAME = "kv";

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        req.result.createObjectStore(STORE_NAME);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        dbPromise = null;
        reject(req.error);
      };
      req.onblocked = () => {
        dbPromise = null;
        reject(new Error("IndexedDB blocked"));
      };
    } catch {
      dbPromise = null;
      reject(new Error("IndexedDB unavailable"));
    }
  });
  return dbPromise;
}

async function idbGet(key: string): Promise<string | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(key);
    req.onsuccess = () => resolve((req.result as string) ?? null);
    req.onerror = () => reject(req.error);
  });
}

async function idbSet(key: string, value: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbRemove(key: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ── Supabase SupportedStorage adapter ───────────────────────

export const persistentStorage = {
  async getItem(key: string): Promise<string | null> {
    try {
      const local = localStorage.getItem(key);
      if (local !== null) return local;
    } catch {
      // localStorage unavailable (private browsing edge case)
    }

    // Recover from IndexedDB
    try {
      const value = await idbGet(key);
      if (value !== null) {
        // Re-hydrate localStorage for fast subsequent reads
        try {
          localStorage.setItem(key, value);
        } catch {
          // quota exceeded or unavailable — fine, we have IDB
        }
      }
      return value;
    } catch {
      return null;
    }
  },

  async setItem(key: string, value: string): Promise<void> {
    try {
      localStorage.setItem(key, value);
    } catch {
      // quota exceeded or unavailable
    }

    try {
      await idbSet(key, value);
    } catch {
      // IndexedDB unavailable — localStorage is the only copy
    }
  },

  async removeItem(key: string): Promise<void> {
    try {
      localStorage.removeItem(key);
    } catch {
      // unavailable
    }

    try {
      await idbRemove(key);
    } catch {
      // IndexedDB unavailable
    }
  },
};
